import React, { useEffect, useContext, useState } from "react";
import { createPortal } from "react-dom";
import { AppContext } from "../../../../../provider/AppProvider";
import { fetchApi } from "../../../../../config/core/Api";
import { AxiosResponse } from "axios";
import BrowserStorage from "../../../../../config/core/BrowserStorage";
import {
  FacebookAlbumsResponse,
  FacebookAlbumPhotosResponse,
} from "../../../../../Types";
import imageComp from "..";
import globalComp from "../../../../../global/components";
import { ImageContext, ModuleContext, ProductContext } from "../../../provider";

export interface UploadFromUrlResponse {
  original: string;
  filtered?: string;
  original_id: number;
  filtered_id: number;
}

interface AlbumPhotos {
  previewUrl: string;
  url: string;
  isSelected: boolean;
}

function FBImages({}): JSX.Element {
  let t = window.INITIAL_DATA.TRANSLATIONS;

  const { facebookResponseData: frd, setFacebookResponseData } = useContext(
    AppContext
  );
  const { isFilteredImage, moduleImages } = useContext(ModuleContext);
  const { addPhotoWithUrl } = useContext(ImageContext);
  const { productType } = useContext(ProductContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [albums, setAlbums] = useState<FacebookAlbumsResponse | null>(null);
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null);
  const [
    albumPhotos,
    setAlbumPhotos,
  ] = useState<FacebookAlbumPhotosResponse | null>(null);
  const [formattedAlbumPhotos, setFormattedAlbumPhotos] = useState<
    AlbumPhotos[]
  >([]);

  const [paginationConfig, setPaginationConfig] = useState<any>({
    previous: null,
    next: null,
  });

  useEffect(() => {
    if (frd) {
      setIsLoading(true);
      // get albums
      fetchApi({
        baseUrl: "https://graph.facebook.com/v7.0",
        url: `/me/?fields=albums{name,picture}&limit=12&access_token=${frd.tokenDetail.accessToken}`,
        method: "GET",
      })
        .then((r: AxiosResponse<FacebookAlbumsResponse>) => {
          if (r.status == 200) {
            setAlbums(r.data);
            if (r.data.albums.paging) {
              setPaginationConfig({
                next: r.data.albums.paging.next
                  ? r.data.albums.paging.next
                  : null,
                previous: r.data.albums.paging.previous
                  ? r.data.albums.paging.previous
                  : null,
                editParentState: setAlbums,
                editSelfState: setPaginationConfig,
              });
            }
          } else {
            fbGeneralFailure();
          }
        })
        .catch((e) => {
          fbGeneralFailure();
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [frd]);

  useEffect(() => {
    if (selectedAlbumId) {
      setIsLoading(true);
      fetchApi({
        baseUrl: "https://graph.facebook.com/v7.0",
        url: `/${selectedAlbumId}/photos?fields=images,picture&limit=12&access_token=${frd.tokenDetail.accessToken}`,
        method: "GET",
      })
        .then((r: AxiosResponse<FacebookAlbumPhotosResponse>) => {
          if (r.status == 200) {
            setAlbumPhotos(r.data);
            if (r.data.paging) {
              setPaginationConfig({
                next: r.data.paging.next ? r.data.paging.next : null,
                previous: r.data.paging.previous
                  ? r.data.paging.previous
                  : null,
                editParentState: setAlbumPhotos,
                editSelfState: setPaginationConfig,
              });
            }
          } else {
            fbGeneralFailure();
          }
        })
        .catch((e) => {
          fbGeneralFailure();
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [selectedAlbumId]);

  useEffect(() => {
    if (albumPhotos) {
      let tempAlbums = [];
      albumPhotos.data.map((elem) => {
        tempAlbums.push({
          previewUrl: elem.picture,
          url: elem.images[0].source,
          isSelected: false,
        });
      });
      setFormattedAlbumPhotos([...tempAlbums]);
    }
  }, [albumPhotos]);

  function handlePhotoSelect(url: string) {
    if (
      moduleImages &&
      ((moduleImages.length == 1 && productType == "text-logo") ||
        (moduleImages.length == 3 && productType == "pop-art"))
    ) {
      window.globalToast({
        type: "warning",
        title: t.warning,
        description: t.dropzone_MaxFilesExceeded,
      });
    } else {
      setIsLoading(true);
      fetchApi({
        method: "POST",
        url: isFilteredImage
          ? "?action=upload_image_from_url&isFiltered=1"
          : "?action=upload_image_from_url",
        data: {
          sources: [url],
        },
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((r: AxiosResponse<UploadFromUrlResponse[]>) => {
          let tempPhotoUrl: AlbumPhotos[] = JSON.parse(
            JSON.stringify(formattedAlbumPhotos)
          );
          tempPhotoUrl.map((elem) => {
            if (elem.url == url) {
              elem.isSelected = !elem.isSelected;
              setFormattedAlbumPhotos([...tempPhotoUrl]);
            }
          });
          addPhotoWithUrl(
            r.data[0],
            r.data[0].filtered_id,
            r.data[0].original_id
          );
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  function fbGeneralFailure() {
    resetFbData();
    window.globalToast({
      type: "error",
      title: t.error,
      description: t.something_went_wrong_facebook,
    });
  }

  function resetFbData() {
    setFacebookResponseData(null);
    setSelectedAlbumId(null);
    setAlbumPhotos(null);
    setFormattedAlbumPhotos([]);
    BrowserStorage.removeStorageItem("fbAuthData");
  }

  return createPortal(
    <div className={"facebook-uploads"}>
      <div
        className={
          "position-relative d-flex justify-content-center align-items-center"
        }
        style={{ minHeight: 150 }}>
        {isLoading ? (
          <div className={"facebook-uploads__loader"}>
            <globalComp.Spinner size={30} color={"white"} />
          </div>
        ) : (
          <imageComp.SNAlbumMessage albumsDetails={albums} data={frd} />
        )}

        {frd && (
          <div className={"facebook-uploads__wrap"}>
            <imageComp.SNHeader
              isBackActionVisible={!!selectedAlbumId}
              backAction={() => {
                setSelectedAlbumId(null);
                setAlbumPhotos(null);
                setFormattedAlbumPhotos([]);
              }}
              logoutAction={() => {
                resetFbData();
                window.FB.logout();
                window.globalToast({
                  type: "success",
                  title: t.success,
                  description: t.success_logout_facebook,
                });
              }}
              userName={`${frd.profile.first_name} ${frd.profile.last_name}`}
            />

            {albums && albums.albums && albums.albums.data.length && selectedAlbumId == null
              ? albums.albums.data.map((element) => (
                  <imageComp.SNAlbumsCell
                    key={element.id}
                    coverUrl={element.picture.data.url}
                    albumName={element.name}
                    onClick={() => {
                      setSelectedAlbumId(element.id);
                    }}
                  />
                ))
              : null}

            {formattedAlbumPhotos.map((photo, index) => (
              <imageComp.ImageCell
                key={`${index}`}
                imageUrl={photo.previewUrl}
                isChecked={photo.isSelected}
                onClick={() => {
                  handlePhotoSelect(photo.url);
                }}
                removeIsCheckedTimeout={2000}
              />
            ))}

            <imageComp.ImagePagination
              paginationConfig={paginationConfig}
              setIsLoading={setIsLoading}
              errorFunc={fbGeneralFailure}
            />
          </div>
        )}
      </div>
    </div>,
    document.getElementById("upload-type-facebook")
  );
}

export default FBImages;
